// Единый менеджер ассетов: листы спрайтов, одиночные картинки и скелеты Spine.
//
// Что и откуда грузить, задаётся явно в src/assets/catalog.ts: адреса файлов, нужные кадры
// и анимации. Менеджер ничего не достраивает и не угадывает: он грузит объявленный набор,
// раскладывает кадры по именам и держит разобранные скелеты. Ассет, которого нет в наборе,
// роняет игру на первом же обращении, а не рисует пустое место.
//
// Звук идёт тем же набором, что и картинки, и той же полосой загрузки. Играет его браузер,
// поэтому менеджер только отдаёт объявленные атласы звуковому движку, см. sound/engine.ts.
//
// Лист спрайтов назван sheet, а не атлас: слово «атлас» занято атласом Spine.
// Импорт пакета spine-pixi регистрирует в Pixi загрузчики атласа и скелета, поэтому он идёт первым.

import '@esotericsoftware/spine-pixi-v8';

import {
  AtlasAttachmentLoader,
  MeshAttachment,
  RegionAttachment,
  SkeletonJson,
  Spine,
  SpineTexture,
  TextureRegion,
  type SkeletonData,
  type SkinEntry,
  type Slot,
  type SlotData,
  type TextureAtlas,
} from '@esotericsoftware/spine-pixi-v8';
import { Assets, type Spritesheet, type Texture, type UnresolvedAsset } from 'pixi.js';

import type { SoundLoader, SoundSpriteSource } from '../sound/engine';

import { upgradeSkeleton, type SkeletonJsonData } from './upgradeSkeleton';

export type { SkeletonData, Slot, Spine, TrackEntry } from '@esotericsoftware/spine-pixi-v8';

/**
 * Описание листа спрайтов: адрес файла и нужные кадры.
 *
 * Ключ в frames — имя кадра в коде, значение — его ключ внутри листа.
 */
export interface TextureSheetSource<TFrame extends string> {
  readonly url: string;
  readonly frames: Readonly<Record<TFrame, string>>;
}

/** Текстуры листа под объявленными именами. Обращение по неизвестному имени не компилируется. */
export type TextureSheet<TFrame extends string> = Readonly<Record<TFrame, Texture>>;

/**
 * Описание скелета Spine: адреса файлов, нужные анимации и лишние слоты.
 *
 * Ключ в animations — имя анимации в коде, значение — её имя внутри скелета.
 */
export interface SpineSkeletonSource<TAnimation extends string> {
  readonly skeletonUrl: string;
  readonly atlasUrl: string;
  readonly animations: Readonly<Record<TAnimation, string>>;

  /** Слоты скелета, которые игра не показывает. Имена взяты из скелета. */
  readonly hiddenSlots?: readonly string[];

  /**
   * Порядок отрисовки слотов снизу вверх. Нет поля — порядок берётся из скелета.
   *
   * Задаётся он один раз, при создании объекта: ключей порядка отрисовки в этих скелетах нет,
   * и анимации его не трогают. Перечислять надо все слоты скелета, иначе часть картинки
   * пропала бы с экрана.
   */
  readonly drawOrder?: readonly string[];

  /**
   * Картинки, которых в атласе скелета нет: слот скелета и адрес файла.
   *
   * Большие картинки в атлас не влезают, и художник отдаёт их отдельными файлами, оставив
   * в скелете сетку с заглушкой. Игра надевает файл на эту сетку, см. BACKGROUND_SKELETON.
   * Один файл можно надеть на несколько слотов: гора на ходу едет двумя.
   */
  readonly textures?: Readonly<Record<string, string>>;
}

/**
 * Описание шрифта: адрес файла и имя семейства.
 *
 * Семейством шрифт ставится в стиль текста, поэтому имя здесь то же, что уйдёт в стиль.
 */
export interface FontSource {
  readonly url: string;
  readonly family: string;
}

/** Набор ассетов, который грузится за один раз. */
export interface AssetBundle {
  readonly images?: readonly string[];
  readonly sheets?: readonly TextureSheetSource<string>[];
  readonly skeletons?: readonly SpineSkeletonSource<string>[];
  readonly fonts?: readonly FontSource[];
  readonly sounds?: readonly SoundSpriteSource<string>[];
}

/** Раскладывает объявленные кадры листа по именам. */
function readSheet<TFrame extends string>(source: TextureSheetSource<TFrame>, sheet: Spritesheet): TextureSheet<TFrame> {
  const frames = {} as Record<TFrame, Texture>;

  for (const [name, key] of Object.entries<string>(source.frames)) {
    const texture: Texture | undefined = sheet.textures[key];
    if (texture === undefined)
      throw new Error(`В листе ${source.url} нет кадра "${key}"`);

    frames[name as TFrame] = texture;
  }

  return frames;
}

/** Разбирает скелет. Ассеты экспортированы из Spine 3.8, рантайм читает 4.x. */
function readSkeleton(raw: SkeletonJsonData, atlas: TextureAtlas): SkeletonData {
  const parser = new SkeletonJson(new AtlasAttachmentLoader(atlas));
  return parser.readSkeletonData(upgradeSkeleton(raw));
}

/**
 * Целая картинка как область текстуры для Spine.
 *
 * Обычно область — это кадр внутри атласа скелета, поэтому у неё есть и поворот, и поля
 * вокруг кадра. Отдельный файл лежит целиком и без поворота, поэтому область покрывает его
 * от угла до угла, а сетка ложится на неё своими же координатами.
 */
function wholeTexture(texture: Texture): TextureRegion {
  const region = new TextureRegion();

  region.texture = SpineTexture.from(texture.source);
  region.u = 0;
  region.v = 0;
  region.u2 = 1;
  region.v2 = 1;
  region.width = texture.width;
  region.height = texture.height;
  region.originalWidth = texture.width;
  region.originalHeight = texture.height;

  return region;
}

/**
 * Одевает сетки скелета картинками, которых нет в его атласе.
 *
 * Скелет разбирается один раз на всю игру, поэтому и одевается он один раз: объекты сцены
 * берут уже одетые сетки.
 *
 * @throws Error если слота в скелете нет, у слота нет ни одной картинки или файл не загружен
 */
function dressSkeleton(skeleton: SkeletonData, source: SpineSkeletonSource<string>, textures: ReadonlyMap<string, Texture>): void {
  for (const [name, url] of Object.entries<string>(source.textures ?? {})) {
    const slot: SlotData | null = skeleton.findSlot(name);
    if (slot === null)
      throw new Error(`В скелете ${source.skeletonUrl} нет слота "${name}"`);

    const texture: Texture | undefined = textures.get(url);
    if (texture === undefined)
      throw new Error(`Картинка ${url} для слота "${name}" скелета ${source.skeletonUrl} не загружена`);

    const region: TextureRegion = wholeTexture(texture);
    const entries: SkinEntry[] = [];

    skeleton.defaultSkin?.getAttachmentsForSlot(slot.index, entries);

    if (entries.length === 0)
      throw new Error(`У слота "${name}" скелета ${source.skeletonUrl} нет картинки, которую надеть`);

    for (const entry of entries) {
      // Заглушку художник ставит сеткой, но слот может держать и обычный кадр: одеваются оба.
      if (!(entry.attachment instanceof MeshAttachment) && !(entry.attachment instanceof RegionAttachment))
        continue;

      entry.attachment.region = region;
      entry.attachment.updateRegion();
    }
  }
}

/** Хранит загруженные ассеты и отдаёт их по описанию из каталога. */
export class AssetManager {
  readonly #textures = new Map<string, Texture>();
  readonly #sheets = new Map<string, TextureSheet<string>>();
  readonly #skeletons = new Map<string, SkeletonData>();

  /** Семейства загруженных шрифтов. Сам шрифт живёт в браузере, игре нужно только имя. */
  readonly #fonts = new Set<string>();

  /** Куда уходят объявленные атласы звуков. */
  readonly #sound: SoundLoader;

  /**
   * @param sound Звуковой движок. Он грузит атласы, а менеджер сводит их прогресс с картинками.
   */
  constructor(sound: SoundLoader) {
    this.#sound = sound;
  }

  /**
   * Грузит набор и сообщает долю загруженного от 0 до 1.
   *
   * Уже загруженный ассет Pixi отдаёт из кэша, разбор листа и скелета тоже не повторяется.
   *
   * Картинки и звук идут разом, а доля у них общая: атлас звука встаёт в счёт одним ассетом,
   * как встаёт лист спрайтов. Считать байты незачем — Pixi их тоже не считает.
   *
   * @throws Error если объявленного кадра в листе нет
   */
  async load(bundle: AssetBundle, onProgress?: (progress: number) => void): Promise<void> {
    const images: readonly string[] = bundle.images ?? [];
    const sheets: readonly TextureSheetSource<string>[] = bundle.sheets ?? [];
    const skeletons: readonly SpineSkeletonSource<string>[] = bundle.skeletons ?? [];
    const fonts: readonly FontSource[] = bundle.fonts ?? [];
    const sounds: readonly SoundSpriteSource<string>[] = bundle.sounds ?? [];

    const urls: string[] = [...images, ...sheets.map((source) => source.url), ...skeletons.flatMap((source) => [source.skeletonUrl, source.atlasUrl])];

    // Шрифт идёт описанием, а не адресом: семейство задаёт игра, из файла его не прочитать.
    const asked: UnresolvedAsset[] = fonts.map((source) => ({ alias: source.url, src: source.url, data: { family: source.family } }));

    const pictures: number = urls.length + asked.length;
    const total: number = pictures + sounds.length;

    let drawn = 0;
    let heard = 0;

    const report = (): void => {
      onProgress?.((drawn * pictures + heard) / total);
    };

    // Pixi грузит разнородные файлы одним вызовом и на нём же считает свою долю. Тип каждого
    // ассета известен из каталога, поэтому он уточняется ниже.
    const loading: Promise<Record<string, unknown>> = Assets.load([...urls, ...asked], (progress: number) => {
      drawn = progress;
      report();
    });

    const sounding: Promise<void> = Promise.all(
      sounds.map((source: SoundSpriteSource<string>) =>
        this.#sound.load(source).then((): void => {
          heard += 1;
          report();
        }),
      ),
    ).then((): void => {});

    const [loaded]: [Record<string, unknown>, void] = await Promise.all([loading, sounding]);

    // Набор загружен целиком. Пустой набор Pixi проходит молча, и без этой строки полоса
    // осталась бы стоять на нуле.
    onProgress?.(1);

    for (const url of images)
      this.#textures.set(url, loaded[url] as Texture);

    for (const source of fonts)
      this.#fonts.add(source.family);

    for (const source of sheets) {
      if (this.#sheets.has(source.url))
        continue;

      this.#sheets.set(source.url, readSheet(source, loaded[source.url] as Spritesheet));
    }

    for (const source of skeletons) {
      if (this.#skeletons.has(source.skeletonUrl))
        continue;

      const raw: SkeletonJsonData = loaded[source.skeletonUrl] as SkeletonJsonData;
      const atlas: TextureAtlas = loaded[source.atlasUrl] as TextureAtlas;

      const skeleton: SkeletonData = readSkeleton(raw, atlas);

      // Картинки уже разложены выше, поэтому сетки скелета одеваются прямо здесь.
      dressSkeleton(skeleton, source, this.#textures);

      this.#skeletons.set(source.skeletonUrl, skeleton);
    }
  }

  /**
   * Текстура одиночной картинки.
   *
   * @throws Error если картинка не загружена
   */
  texture(url: string): Texture {
    const texture: Texture | undefined = this.#textures.get(url);
    if (texture === undefined)
      throw new Error(`Картинка ${url} не загружена: её нет ни в одном загруженном наборе`);

    return texture;
  }

  /**
   * Кадры листа спрайтов.
   *
   * @throws Error если лист не загружен
   */
  sheet<TFrame extends string>(source: TextureSheetSource<TFrame>): TextureSheet<TFrame> {
    const sheet: TextureSheet<string> | undefined = this.#sheets.get(source.url);
    if (sheet === undefined)
      throw new Error(`Лист ${source.url} не загружен: его нет ни в одном загруженном наборе`);

    return sheet as TextureSheet<TFrame>;
  }

  /**
   * Семейство загруженного шрифта. Им шрифт ставится в стиль текста.
   *
   * @throws Error если шрифт не загружен
   */
  font(source: FontSource): string {
    if (!this.#fonts.has(source.family))
      throw new Error(`Шрифт ${source.url} не загружен: его нет ни в одном загруженном наборе`);

    return source.family;
  }

  /**
   * Новый объект сцены по скелету. Разобранный скелет общий для всех таких объектов.
   *
   * Объявленные лишние слоты снимаются сразу: их картинки лежат в скелете, но игре не нужны.
   * Объявленный порядок отрисовки ставится там же, разом на всю жизнь объекта.
   *
   * @throws Error если скелет не загружен, в нём нет объявленного слота или объявленный
   * порядок отрисовки перечисляет не все слоты скелета
   */
  spine(source: SpineSkeletonSource<string>): Spine {
    const skeleton: SkeletonData | undefined = this.#skeletons.get(source.skeletonUrl);
    if (skeleton === undefined)
      throw new Error(`Скелет ${source.skeletonUrl} не загружен: его нет ни в одном наборе`);

    const spine = new Spine(skeleton);

    for (const name of source.hiddenSlots ?? [])
      slotOf(spine, source, name).setAttachment(null);

    if (source.drawOrder !== undefined) {
      const order: Slot[] = source.drawOrder.map((name: string) => slotOf(spine, source, name));

      if (order.length !== spine.skeleton.slots.length)
        throw new Error(`Порядок отрисовки скелета ${source.skeletonUrl} перечисляет ${order.length} слотов из ${spine.skeleton.slots.length}`);

      spine.skeleton.drawOrder = order;
    }

    return spine;
  }
}

/**
 * Слот скелета по имени.
 *
 * @throws Error если такого слота в скелете нет
 */
function slotOf(spine: Spine, source: SpineSkeletonSource<string>, name: string): Slot {
  const slot: Slot | null = spine.skeleton.findSlot(name);
  if (slot === null)
    throw new Error(`В скелете ${source.skeletonUrl} нет слота "${name}"`);

  return slot;
}
